import React, { useState, useMemo } from 'react';
import { AssessmentPoint } from '../../types';
import { PageHeader, StatsCards } from '../../components/Common';
import { StatCard } from '../../components/Common/StatsCards';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useAuth } from '../../contexts/AuthContext';
import { useCandidates } from '../../hooks/useCandidates';
import { useCandidateRatings } from '../../hooks/useCandidateRatings';
import { candidateRatingsService, candidatesService } from '../../services';
import { useToast } from '../../contexts/ToastContext';
import {
  useAssessmentPoints,
  useCreateAssessmentPoint,
  useUpdateAssessmentPoint,
  useDeleteAssessmentPoint
} from '../../hooks/useAssessmentPoints';
import {
  CandidateEvaluation,
  buildDefaultPoints,
  getStatusFromPercentage,
  groupByCategory,
  printAssessmentSheet,
  mapEvaluationForExport,
  mapPointForExport,
} from './evaluationTypes';
import EvaluationList from './EvaluationList';
import EvaluationForm from './EvaluationForm';
import EvaluationDetails from './EvaluationDetails';
import AssessmentPointsTab from './AssessmentPointsTab';

const CandidatesEvaluation: React.FC = () => {
  const { user } = useAuth();
  const { data: candidates = [] } = useCandidates();
  const { data: assessmentPoints = [], isLoading: pointsLoading, error: pointsError } = useAssessmentPoints();
  const createPointMutation = useCreateAssessmentPoint();
  const updatePointMutation = useUpdateAssessmentPoint();
  const deletePointMutation = useDeleteAssessmentPoint();

  // Candidate evaluations only use candidate-facing points —
  // employee-only points belong to the Crew Evaluation module.
  const effectiveCriteria = useMemo(() => {
    const candidatePoints = assessmentPoints.filter(p => p.applicableFor !== 'employees');
    return candidatePoints.length > 0 ? candidatePoints : buildDefaultPoints();
  }, [assessmentPoints]);

  const [activeTab, setActiveTab] = useState<'evaluations' | 'points'>('evaluations');
  const toast = useToast();
  const ratingsQueryOptions = useMemo(() => ({ populate: '*' }), []);
  const { data: candidateRatings = [], refetch: refetchRatings } = useCandidateRatings(ratingsQueryOptions);
  const [showEvaluationForm, setShowEvaluationForm] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<CandidateEvaluation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [showPointsForm, setShowPointsForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState<AssessmentPoint | undefined>();
  const [pointsSearchTerm, setPointsSearchTerm] = useState('');

  const [candidateId, setCandidateId] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [evaluatorName, setEvaluatorName] = useState('');
  const [ratings, setRatings] = useState<{ [key: string]: number }>({});
  const [notes, setNotes] = useState('');

  // Evaluations are persisted as candidate-rating rows — one row per assessed
  // point sharing the same candidate + evaluatedAt batch timestamp. Rebuild the
  // evaluation cards by grouping those rows back together.
  const evaluations = useMemo<CandidateEvaluation[]>(() => {
    const candidateKeyOf = (r: any): string => {
      const c = r.candidateData || r.candidate;
      if (c && typeof c === 'object') return String(c.documentId || c.id || '');
      return String(c ?? '');
    };
    const groups = new Map<string, any[]>();
    (candidateRatings as any[]).forEach((r) => {
      const key = `${candidateKeyOf(r)}|${r.evaluatedAt}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries()).map(([key, rows]) => {
      const first = rows[0];
      const cid = candidateKeyOf(first);
      const rel: any = first.candidateData || (typeof first.candidate === 'object' ? first.candidate : null);
      const candidateObj = candidates.find((c: any) => String(c.documentId || c.id) === cid);
      const ratingMap: { [pointId: string]: number } = {};
      rows.forEach((r) => {
        const pt = effectiveCriteria.find((p) =>
          p.name === r.pointName ||
          (r.assessmentPoint && String(r.assessmentPoint?.documentId || r.assessmentPoint) === String(p.documentId || p.id))
        );
        ratingMap[pt ? pt.id : r.pointName] = r.score;
      });
      const totalScore = rows.reduce((s, r) => s + (r.score || 0), 0);
      const maxScore = rows.length * 4;
      const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
      const relName = rel ? `${rel.firstName || ''} ${rel.lastName || ''}`.trim() : '';
      return {
        id: key,
        candidateId: cid,
        candidateName: relName || (candidateObj ? `${candidateObj.firstName} ${candidateObj.lastName}` : '-'),
        jobTitle: candidateObj?.appliedPosition || rel?.appliedPosition || '-',
        evaluationDate: (first.evaluatedAt || '').split('T')[0],
        evaluatorName: first.evaluatedBy || '-',
        totalScore, maxScore, percentage,
        status: getStatusFromPercentage(percentage),
        ratings: rows,
        ratingMap,
        notes: first.notes || '',
      };
    }).sort((a, b) => b.evaluationDate.localeCompare(a.evaluationDate));
  }, [candidateRatings, candidates, effectiveCriteria]);

  const currentUserName = useMemo(() => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    return name || user?.email || '';
  }, [user]);

  const handleCandidateSelect = (id: string) => {
    setCandidateId(id);
    const candidate = candidates.find(c => String(c.documentId || c.id) === id);
    if (candidate) {
      setJobTitle(candidate.appliedPosition || '');
    }
  };

  const filteredEvaluations = useMemo(() => {
    return evaluations.filter(evaluation => {
      const matchesSearch = !searchTerm || (
        evaluation.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluation.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluation.evaluatorName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || evaluation.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [evaluations, searchTerm, statusFilter]);

  const filteredPoints = useMemo(() => {
    return assessmentPoints.filter(point => {
      return !pointsSearchTerm || (
        point.name.toLowerCase().includes(pointsSearchTerm.toLowerCase()) ||
        point.description?.toLowerCase().includes(pointsSearchTerm.toLowerCase()) ||
        point.category.toLowerCase().includes(pointsSearchTerm.toLowerCase())
      );
    });
  }, [assessmentPoints, pointsSearchTerm]);

  const groupedCriteria = useMemo(() => {
    return groupByCategory(effectiveCriteria.filter(p => p.isActive));
  }, [effectiveCriteria]);

  const handleAddEvaluation = () => {
    setCandidateId('');
    setJobTitle('');
    setEvaluatorName(currentUserName);
    setRatings({});
    setNotes('');
    setSelectedEvaluation(null);
    setShowEvaluationForm(true);
  };

  const handleViewEvaluation = (evaluation: CandidateEvaluation) => {
    setSelectedEvaluation(evaluation);
    setShowEvaluationForm(false);
  };

  const handleRatingChange = (pointId: string, score: number) => {
    setRatings(prev => ({ ...prev, [pointId]: score }));
  };

  const handleSubmitEvaluation = async () => {
    if (!candidateId) {
      toast.error('Please select a candidate first');
      return;
    }
    const activeCriteria = effectiveCriteria.filter(p => p.isActive);
    const totalScore = activeCriteria.reduce((sum, point) => sum + (ratings[point.id] || 0), 0);
    const maxScore = activeCriteria.length * 4;
    const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    const evaluatedAt = new Date().toISOString();
    const evaluator = evaluatorName || currentUserName;
    const candidateObj = candidates.find((c: any) => String(c.documentId || c.id) === candidateId);

    try {
      // One candidate-rating row per assessed point (score 0 for unrated ones
      // keeps maxScore exact when the evaluation is rebuilt on next visit).
      await Promise.all(activeCriteria.map(point =>
        candidateRatingsService.create({
          candidateId,
          assessmentPointId: point.documentId || undefined,
          pointName: point.name,
          score: ratings[point.id] || 0,
          evaluatedBy: evaluator,
          evaluatedAt,
          notes,
        })
      ));

      // Keep the candidate's total rating in sync for the Candidates module.
      if (candidateObj) {
        try {
          await candidatesService.update(candidateObj.id as any, {
            documentId: candidateObj.documentId,
            totalRating: Math.round(percentage * 10) / 10,
          });
        } catch (e) {
          console.error('Failed to update candidate total rating:', e);
        }
      }

      await refetchRatings();
      toast.success('Evaluation saved');
      setShowEvaluationForm(false);
    } catch (e) {
      console.error('Failed to save evaluation:', e);
      toast.error('Failed to save evaluation');
    }
  };

  const handleExport = () => {
    exportToExcel(filteredEvaluations.map(mapEvaluationForExport), `candidates_evaluation_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    printAsExcel(filteredEvaluations.map(mapEvaluationForExport), 'Candidates Evaluation');
  };

  const handlePrintAssessmentSheet = () => {
    if (selectedEvaluation) {
      printAssessmentSheet(selectedEvaluation, effectiveCriteria.filter(p => p.isActive));
    }
  };

  const handleAddPoint = () => { setEditingPoint(undefined); setShowPointsForm(true); };
  const handleEditPoint = (point: AssessmentPoint) => { setEditingPoint(point); setShowPointsForm(true); };
  const handleDeletePoint = async (point: AssessmentPoint) => {
    if (window.confirm(`Are you sure you want to delete the assessment point "${point.name}"?`)) {
      await deletePointMutation.execute(point.documentId || point.id);
    }
  };

  // An evaluation is a group of candidate-rating rows sharing candidate +
  // evaluatedAt — deleting means removing every row of the batch.
  const handleDeleteEvaluation = async (evaluation: CandidateEvaluation) => {
    const name = evaluation.candidateName !== '-' ? evaluation.candidateName : 'this evaluation';
    if (!window.confirm(`Delete the evaluation for ${name} (${new Date(evaluation.evaluationDate).toLocaleDateString()})? This removes all ${evaluation.ratings.length} rating rows.`)) return;
    try {
      await Promise.all(evaluation.ratings.map((r: any) => candidateRatingsService.delete(r.documentId || r.id)));
      toast.success('Evaluation deleted');
      await refetchRatings();
    } catch (e) {
      console.error('Failed to delete evaluation:', e);
      toast.error('Failed to delete evaluation');
    }
  };

  const handlePointFormSubmit = async (pointData: Partial<AssessmentPoint>) => {
    if (editingPoint) {
      await updatePointMutation.execute({
        id: editingPoint.documentId || editingPoint.id,
        data: { ...pointData, documentId: editingPoint.documentId }
      });
    } else {
      await createPointMutation.execute(pointData);
    }
    setShowPointsForm(false);
    setEditingPoint(undefined);
  };

  const handlePointFormCancel = () => { setShowPointsForm(false); setEditingPoint(undefined); };

  const handleExportPoints = () => {
    exportToExcel(filteredPoints.map(mapPointForExport), `assessment_points_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrintPoints = () => {
    printAsExcel(filteredPoints.map(mapPointForExport), 'Assessment Points');
  };

  const evaluationStats: StatCard[] = [
    { label: 'Excellent', value: evaluations.filter(e => e.status === 'excellent').length, color: 'green' },
    { label: 'Good', value: evaluations.filter(e => e.status === 'good').length, color: 'blue' },
    { label: 'Average', value: evaluations.filter(e => e.status === 'average').length, color: 'yellow' },
    { label: 'Unsatisfactory', value: evaluations.filter(e => e.status === 'unsatisfactory').length, color: 'red' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Candidates Evaluation"
        description="Evaluate job candidates based on assessment points and criteria."
        actions={[
          { label: 'Export', onClick: activeTab === 'evaluations' ? handleExport : handleExportPoints, variant: 'success' },
          { label: 'Print', onClick: activeTab === 'evaluations' ? handlePrint : handlePrintPoints, variant: 'info' },
        ]}
      />

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('evaluations')}
            className={`${
              activeTab === 'evaluations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Evaluations
          </button>
          <button
            onClick={() => setActiveTab('points')}
            className={`${
              activeTab === 'points'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Assessment Points
          </button>
        </nav>
      </div>

      {activeTab === 'evaluations' && (
        <>
          <StatsCards stats={evaluationStats} />

          {!showEvaluationForm && !selectedEvaluation && (
            <EvaluationList
              evaluations={evaluations}
              filteredEvaluations={filteredEvaluations}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onAdd={handleAddEvaluation}
              onView={handleViewEvaluation}
              onDelete={handleDeleteEvaluation}
              onExport={handleExport}
              onPrint={handlePrint}
            />
          )}

          {showEvaluationForm && (
            <EvaluationForm
              candidates={candidates}
              candidateId={candidateId}
              onCandidateSelect={handleCandidateSelect}
              jobTitle={jobTitle} setJobTitle={setJobTitle}
              evaluatorName={evaluatorName || currentUserName}
              ratings={ratings} onRatingChange={handleRatingChange}
              notes={notes} setNotes={setNotes}
              groupedCriteria={groupedCriteria}
              onSubmit={handleSubmitEvaluation}
              onCancel={() => setShowEvaluationForm(false)}
            />
          )}

          {selectedEvaluation && !showEvaluationForm && (
            <EvaluationDetails
              evaluation={selectedEvaluation}
              groupedCriteria={groupedCriteria}
              onPrintAssessmentSheet={handlePrintAssessmentSheet}
              onClose={() => setSelectedEvaluation(null)}
            />
          )}
        </>
      )}

      {activeTab === 'points' && (
        <AssessmentPointsTab
          assessmentPoints={assessmentPoints}
          filteredPoints={filteredPoints}
          pointsLoading={pointsLoading}
          pointsError={pointsError}
          pointsSearchTerm={pointsSearchTerm}
          onPointsSearchChange={setPointsSearchTerm}
          showPointsForm={showPointsForm}
          editingPoint={editingPoint}
          onAddPoint={handleAddPoint}
          onEditPoint={handleEditPoint}
          onDeletePoint={handleDeletePoint}
          onPointFormSubmit={handlePointFormSubmit}
          onPointFormCancel={handlePointFormCancel}
          onExport={handleExportPoints}
          onPrint={handlePrintPoints}
        />
      )}
    </div>
  );
};

export default CandidatesEvaluation;
